<template>
  <div
    v-if="active"
    :class="['m-market-item', {'isFold': !shouldExpand}]"
  >
    <div class="m-market-item--header">
      <h1 class="m-market-item--name">
        <a
          class="tw-flex tw-items-center"
          @click="handleExpand"
        >
          <span class="m-control-btn m-collapse-btn">
            <i
              class="m-icon-trangle down"
              :class="{
                active: !shouldExpand
              }"
            />
          </span>
          <span
            class="tw-line-clamp-2"
          >{{ getMarketTitle(marketInfo) }}</span>
        </a>
        <svg-icon
          v-if="!marketInfo.comboType"
          icon-class="icon-gantanhao"
          class="m-icon-guide"
          @click="showGuidance"
        />

        <EarlyPayout
          :market="marketInfo"
        />
      </h1>

      <div
        v-if="!marketInfo.comboType"
        class="m-control-btns"
      >
        <BtnStats
          v-if="hasWidgetBtn"
          class="m-control-btn"
          :active="widgetVisible"
          @click="toggleWidget"
        />

        <IconFavorite
          class="m-control-btn x-link-active"
          :active="isFavorite"
          width="14px"
          @click="handleFavourite"
        />
      </div>
    </div>

    <Widgets
      v-if="widgetVisible"
      name="betAssist"
      :event-id="event.eventId"
      :sport-id="event.sportId"
      :market-id="marketInfo.id"
      :specifiers="marketInfo.specifiers"
    />

    <div
      v-show="shouldExpand"
      ref="marketContent"
      v-block-loading:fetchMarket="loading"
      class="m-market-item--content"
    >
      <template v-if="loading === false">
        <template v-if="isSpecifiers || [1, 7, 8, 9, 10, 11].includes(market.presentationType)">
          <template v-if="marketInfo.presentationType === 7">
            <Market7Aggregate
              v-if="hasOutcomes(marketInfo)"
              :markets="market"
              :event="event"
            />
          </template>

          <template v-else-if="marketInfo.presentationType === 8">
            <Market8Range
              v-if="hasOutcomes(marketInfo)"
              :market="market"
              :event="event"
              :visible="shouldExpand"
            />
          </template>

          <template v-else-if="marketInfo.presentationType === 9">
            <Market9Players
              v-if="hasOutcomes(marketInfo)"
              :markets="market"
              :event="event"
            />
          </template>

          <template v-else-if="marketInfo.presentationType === 10">
            <Market10Handicap
              v-if="hasOutcomes(marketInfo)"
              :markets="market"
              :event="event"
            />
          </template>

          <template v-else-if="marketInfo.presentationType === 11">
            <Market11XTotal
              v-if="hasOutcomes(marketInfo)"
              :markets="market"
              :event="event"
            />
          </template>

          <template v-else>
            <Market1Specifier
              v-if="isSpecifiers ? hasOutcomes(marketInfo) : hasOutcomes(market)"
              :markets="isSpecifiers ? market : [market]"
              :event="event"
            />
          </template>
        </template>
        <template v-else>
          <component
            :is="getCurrentMarketComponent(market.presentationType)"
            v-if="hasOutcomes(market)"
            :market="market"
            :event="event"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { ADDED_FAVOURITES, REMOVED_FAVOURITES } from '@ms/config/message'
import { useAuthStore } from '@ms/stores/auth'
import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import { useWapBetslipStore } from 'wap/stores/betslip'
import { fetchMarketGuide } from '@ms/api/match'
import toast from 'fe-vui/toast'
import eventBus from '@ms/utils/event-bus.js'
import { getMarketTitle } from '@ms/components/matchDetail/market.js'
import BtnStats from '@ms/components/BtnStats.vue'
import Market1Specifier from '../components/Market1Specifier'
import Market2ThreeWay from '../components/Market2ThreeWay'
import Market3SingleLine from '../components/Market3SingleLine'
import Market4PartDesc from '../components/Market4PartDesc'
import Market5Normal from '../components/Market5Normal'
import Market6Comb from '../components/Market6Comb'
import Market7Aggregate from '../components/Market7Aggregate'
import Market8Range from 'wap/match/markets/Market8Range.vue'
import Market9Players from '../components/Market9Players'
import Market10Handicap from '../components/Market10Handicap.vue'
import Market11XTotal from '../components/Market11XTotal'
import modalHelper from '@ms/utils/scroll-fixed'
import Widgets from 'base/components/Widgets'
import EarlyPayout from '@ms/components/market/EarlyPayout.vue'
import { getMarketType } from 'base/components/Widgets/name.js'
import { debounce } from 'throttle-debounce'
import IconFavorite from '@ms/components/IconFavorite.vue'
import { openDialog } from '@ms/ui/dialog'
import { cleanHtml } from '@ms/utils/safe'
import 'base/icons/svg/gantanhao.svg'

export default {
  name: 'MarketItem',
  components: {
    BtnStats,
    Market1Specifier,
    Market2ThreeWay,
    Market3SingleLine,
    Market4PartDesc,
    Market5Normal,
    Market6Comb,
    Market7Aggregate,
    Market9Players,
    Market8Range,
    Market10Handicap,
    Market11XTotal,
    Widgets,
    IconFavorite,
    EarlyPayout
  },
  props: {
    market: {
      type: [Object, Array],
      default: () => ({})
    },
    marketId: {
      type: [Number, String],
      default: ''
    },
    shouldExpand: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const marketContent = ref(null)
    const { igopmsMarkets } = storeToRefs(useGlobalConfigStore())
    const matchDetailStore = useMatchDetailStore()
    const { event, favouriteMarketIds } = storeToRefs(matchDetailStore)

    const { toggleFavouriteMarket, fetchMarketInfo, subBlacklist } = matchDetailStore

    const { selectionList } = storeToRefs(useWapBetslipStore())

    return {
      getMarketTitle,
      igopmsMarkets,
      marketContent,
      event,
      favouriteMarketIds,
      toggleFavouriteMarket,
      fetchMarketInfo,
      subBlacklist,
      selectionList
    }
  },
  data() {
    return {
      marketInfo: {},
      isSpecifiers: false,
      widgetVisible: false,
      loading: false,
      hasFetched: false,
      active: false,
      isSingleExpand: false
    }
  },
  computed: {
    isFavorite() {
      return this.favouriteMarketIds.includes(+this.marketInfo.id)
    },
    hasWidgetBtn() {
      return !!getMarketType(this.marketInfo.id, this.event.sportId, this.marketInfo.specifiers)
    },
    shouldFetch() {
      return !this.hasFetched && this.igopmsMarkets.includes(`${this.marketInfo.id}`) && this.marketInfo.presentationType !== 1 && this.isSingleExpand && !this.marketInfo.expand
    },
    checkIsActive() {
      let val = 0
      if (Array.isArray(this.market)) {
        if (this.market.every((x) => +x.status === 2)) {
          val = 2
        } else if (this.market.every((x) => +x.status === 3)) {
          val = 3
        } else if (this.market.some((x) => +x.status === 0 || +x.status === 1)) {
          val = 0
        } else {
          // 防止因状态不统一导致outcome已隐藏而descreption和标题还存在
          val = 3
        }
      } else {
        val = this.market.status
      }
      return val
    }
  },
  watch: {
    checkIsActive: {
      immediate: true,
      handler(val) {
        clearTimeout(this._timer)
        if (val === 2) {
          this._timer = setTimeout(() => {
            this.active = false
            eventBus.$emit('update-mini-footer')
          }, 5000)
        } else if (val === 3) {
          this.active = false
        } else {
          this.active = true
        }
        eventBus.$emit('update-mini-footer')
      }
    },
    shouldExpand(currShouldExpand) {
      if (currShouldExpand) {
        this.shouldFetch && this.fetchMarket()
        this.widgetVisible = false
      }
    },
    active: {
      immediate: true,
      handler(currActive) {
        this.$emit('active-change', {
          marketId: this.marketId,
          active: currActive
        })
      }
    }
  },
  created() {
    this.isSpecifiers = Array.isArray(this.market)
    const marketInfo = this.isSpecifiers ? this.market[0] : this.market
    this.marketInfo = marketInfo

    const betslipMarket = this.betslipMarketFilter()
    if (betslipMarket.includes(`${this.marketId}`)) this.hasFetched = true
  },
  methods: {
    // 判断 market 中是否有 outcome 存在, 有可能新增 market 没有 outcomes
    hasOutcomes(market = {}) {
      const { outcomes } = market
      return outcomes && outcomes.order && outcomes.order.length
    },
    toggleWidget() {
      this.widgetVisible = !this.widgetVisible
      window.mstrace('MatchDetail_BetAssist')
    },
    handleFavourite: debounce(1000, true, async function () {
      if (!useAuthStore().loginStatus) {
        return this.$login()
      }

      try {
        const status = !this.isFavorite
        await this.toggleFavouriteMarket({
          sportId: this.event.sportId,
          marketId: this.marketInfo.id,
          status
        })
        if (status) {
          toast(ADDED_FAVOURITES)
        } else {
          toast(REMOVED_FAVOURITES)
        }
        this.$emit('favourite', status)
      } catch (err) {
        console.error(err)
        if (err.login === false) {
          this.$login()
        }
      }
      window.mstrace('MatchDetail_Favorite', 'MatchDetail')
    }),
    handleExpand() {
      this.isSingleExpand = true
      this.$emit('toggle-expand', {
        marketId: this.marketId
      })
    },
    async fetchMarket() {
      this.loading = true
      try {
        const body = { marketId: this.marketInfo.id }
        if (!this.isSpecifiers) {
          body.specifiers = this.marketInfo.specifiers
        }
        await this.fetchMarketInfo(body)
        this.hasFetched = true
        this.loading = false
      } catch (error) {
        this.loading = -1
        console.error(error)
      }
    },
    async showGuidance() {
      let message = this.marketInfo.guidance
      if (!message) {
        message = await fetchMarketGuide({
          sportId: this.event.sportId,
          marketId: this.marketInfo.id,
          product: this.marketInfo.product
        })
      }
      const isHtml = /<div|<p|<span/.test(message)
      const classNames = []
      if (isHtml) {
        classNames.push('dialog-market-html')
        message = cleanHtml(message)
      }

      openDialog({
        message,
        classNames,
        closeOnClickModal: true,
        dangerouslyUseHTMLString: isHtml,
        onClose: () => {
          modalHelper.beforeClose()
        }
      })

      modalHelper.afterOpen()
    },
    getCurrentMarketComponent(presentationType) {
      switch (presentationType) {
        case 2:
          return 'Market2ThreeWay'
        case 3:
          return 'Market3SingleLine'
        case 4:
          return 'Market4PartDesc'
        case 6:
          return 'Market6Comb'
        default:
          return 'Market5Normal'
      }
    },
    betslipMarketFilter() {
      if (this.selectionList.length) {
        const ids = this.selectionList.map((x) => {
          const id = `${x.marketInfo.id || ''}`
          return x.marketInfo.specifiers ? `${id}_${x.marketInfo.specifiers}` : id
        })
        return [...new Set(ids)]
      }
      return []
    },
    sub(isSub = false) {
      this.subBlacklist({
        isBlack: false,
        isSub,
        marketIds: [this.marketInfo.id],
        specifiers: this.marketInfo.specifiers
      })
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");
@import url("~wap/common/style/mixin.less");

.m-market-item {
  padding: 10px;
  border-radius: 12px;
  margin-top: 6px;
  background: #161616;

  &--header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    line-height: 16px;

    &.isFold {
      height: 28px;
      margin-bottom: -0;
    }
  }

  &--name {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: #fff;
    font-size: 14px;
    line-height: 17px;
  }

  .m-widgets {
    margin-bottom: 10/@rem;
  }

  .m-control-btns {
    display: flex;
    width: 80/@rem;
    align-items: center;
    justify-content: flex-end;
    margin-right: -8px;

    .m-control-btn {
      padding: 0 8px;

      img {
        width: 12px !important;
        height: 12px !important;
      }
    }
  }

  .only-icon {
    width: 24/@rem;
  }

  .m-collapse-btn {
    display: inline-block;
    width: 16/@rem;
    height: 16/@rem;
    flex-shrink: 0;
    margin-right: 4px;
    line-height: 16/@rem;
    text-align: center;

    .m-icon-trangle {
      .m-icon-trangle(5/@rem; #666);

      transition: transform 0.2s linear;

      &.active {
        transform: rotate(-90deg);
      }
    }
  }

  .m-market-specifier {
    margin-top: -5px;
  }

  .m-icon-guide {
    flex-shrink: 0;
    padding: 0 3px 0 5px;
    color: #999;
    font-size: 16px;
    line-height: 44px;
  }

  .m-event-early {
    &-item {
      margin-left: 10px;
    }
  }

  &.isFold {
    padding-bottom: 0;

    .m-market-item--header {
      height: 28px;
      padding-bottom: 10px;
      margin-bottom: 6px;
    }
  }
}

.dialog-market-html {
  .ui-dialog {
    padding-top: 0;

    .ui-dialog--body {
      padding-bottom: 0;

      ul,
      ol,
      li {
        list-style: initial;
      }

      li {
        margin: 10px 0;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.m-market-item ::v-deep {
  .m-block-loading {
    min-height: 110px;
  }

  .m-retry-comp {
    padding: 0;
  }

  .v-retry-img.imgSmall {
    width: 60px;
  }

  .v-retry-main.failSmall .fail-text {
    color: #666;
    font-size: 14px;
  }

  .retry-btn.btnSmall {
    margin-top: 8px;
  }

  .retry-btn {
    width: 110px;
    height: 28px;
    line-height: 28px;
  }
}

[data-mode="light"] .m-market-item {
  background: #fff;

  &--name {
    color: #000;
  }

  .m-icon-trangle.down::before {
    border-top-color: #999;
  }

  &:last-child {
    border-bottom: none
  }

  ::v-deep .btn-stats .inline-svg.active {
    color: #ffca27;
  }
}
</style>
